import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { startOfDay } from "date-fns"
import { Calendar } from "lucide-react"
import { type UseFormReturn, useForm, useWatch } from "react-hook-form"
import { z } from "zod"

import { BookingsService } from "@/client"
import DietaryRequirementsForm from "@/components/Booking/DietaryRequirementsForm"
import GuestInformationForm from "@/components/Booking/GuestInformationForm"
import KayakRentalForm from "@/components/Booking/KayakRentalForm"
import WaterTaxiForm from "@/components/Booking/WaterTaxiForm"
import { Form } from "@/components/ui/form"
import { LoadingButton } from "@/components/ui/loading-button"
import {
  ALLERGY_SEVERITY_VALUES,
  DIETARY_TYPE_VALUES,
  EQUIPMENT_SIZE_VALUES,
  EQUIPMENT_TYPE_VALUES,
  SCHEDULED_TIME_VALUES,
  TRIP_TYPE_VALUES,
} from "@/constants/booking"
import useCustomToast from "@/hooks/useCustomToast"
import {
  bookingFormValuesToRequest,
  formatLodgeStayFormRange,
} from "@/lib/booking-mappers"
import { getLodgeStayValidationIssues } from "@/lib/booking-validation"

export const Route = createFileRoute("/_layout/bookings/new")({
  component: NewBookingPage,
  staticData: {
    pageTitle: "New Booking",
    pageIcon: Calendar,
    pageIconClassName: "text-primary",
  },
  head: () => ({
    meta: [
      {
        title: "New Booking - Broken Islands Booking",
      },
    ],
  }),
})

/**
 * Get today's date with the time set to the start of the day.
 *
 * @returns A Date representing today with the time normalized to 00:00:00
 */
function getToday(): Date {
  return startOfDay(new Date())
}

// Dietary requirement schema
const dietaryRequirementSchema = z.object({
  guestName: z.string().min(1, "Guest name is required"),
  meals: z.object({
    breakfast: z.boolean(),
    lunch: z.boolean(),
    dinner: z.boolean(),
  }),
  camperLunches: z.object({
    arrival: z.boolean(),
    departure: z.boolean(),
  }),
  dietaryType: z.enum(DIETARY_TYPE_VALUES),
  allergies: z.string().optional(),
  allergySeverity: z.enum(ALLERGY_SEVERITY_VALUES).optional(),
  notes: z.string().optional(),
})

// Kayak rental schema
const kayakRentalSchema = z
  .object({
    guestName: z.string().min(1, "Guest name is required"),
    equipmentType: z.enum(EQUIPMENT_TYPE_VALUES, {
      message: "Equipment type is required",
    }),
    equipmentSize: z.enum(EQUIPMENT_SIZE_VALUES, {
      message: "Equipment size is required",
    }),
    pickupDate: z.date({ message: "Pickup date is required" }),
    pickupTime: z.string().optional(),
    returnDate: z.date({ message: "Return date is required" }),
    returnTime: z.string().optional(),
    notes: z.string().optional(),
  })
  .refine(
    (data) => {
      return startOfDay(data.returnDate) >= startOfDay(data.pickupDate)
    },
    {
      message: "Return date must be on or after pickup date",
      path: ["returnDate"],
    },
  )

// Water taxi trip schema (replaces waterTaxiTransferSchema)
const waterTaxiTripSchema = z
  .object({
    tripType: z.enum(TRIP_TYPE_VALUES, {
      message: "Trip type is required",
    }),
    timeSelection: z.enum(["scheduled", "custom"], {
      message: "Time selection is required",
    }),
    scheduledTime: z.enum(SCHEDULED_TIME_VALUES).optional(),
    customTime: z.string().optional(),
    pickupLocation: z.string().optional(),
    dropoffLocation: z.string().optional(),
    passengerCount: z
      .number({ message: "Passenger count is required" })
      .min(1, "At least 1 passenger is required")
      .max(12, "Water taxi capacity is 12 maximum"),
    kayakCount: z
      .number()
      .min(0, "Kayak count cannot be negative")
      .max(12, "Maximum 12 kayaks per taxi"),
    notes: z.string().optional(),
  })
  .refine(
    (data) => {
      // Scheduled time selection requires scheduled time
      if (data.timeSelection === "scheduled") {
        return !!data.scheduledTime
      }
      return true
    },
    {
      message: "Scheduled time is required for scheduled transfers",
      path: ["scheduledTime"],
    },
  )
  .refine(
    (data) => {
      // Custom time selection requires custom time
      if (data.timeSelection === "custom") {
        return !!data.customTime
      }
      return true
    },
    {
      message: "Custom time is required for custom transfers",
      path: ["customTime"],
    },
  )
  .refine(
    (data) => {
      // Pickup location required when tripType is custom OR timeSelection is custom
      if (data.tripType === "custom" || data.timeSelection === "custom") {
        return !!data.pickupLocation && data.pickupLocation.length > 0
      }
      return true
    },
    {
      message: "Pickup location is required for custom trips or times",
      path: ["pickupLocation"],
    },
  )
  .refine(
    (data) => {
      // Dropoff location required when tripType is custom OR timeSelection is custom
      if (data.tripType === "custom" || data.timeSelection === "custom") {
        return !!data.dropoffLocation && data.dropoffLocation.length > 0
      }
      return true
    },
    {
      message: "Dropoff location is required for custom trips or times",
      path: ["dropoffLocation"],
    },
  )

// Zod schema with validation rules for guest information and water taxi
export const bookingFormSchema = z
  .object({
    // Guest Information fields
    guestType: z.enum(["lodge", "camping", "mixed"], {
      message: "Guest type is required",
    }),
    primaryContactName: z
      .string()
      .min(1, "Primary contact name is required")
      .max(200, "Name must be 200 characters or less"),
    email: z
      .string()
      .min(1, "Email is required")
      .email("Please enter a valid email address"),
    phone: z.string().optional(),
    arrivalDate: z.date({ message: "Arrival date is required" }),
    departureDate: z.date({ message: "Departure date is required" }),
    numberOfGuests: z
      .number({ message: "Number of guests is required" })
      .min(1, "At least 1 guest is required")
      .max(50, "Maximum 50 guests allowed"),
    lodgeStays: z.array(
      z.object({
        startDate: z.date({ message: "Lodge start date is required" }),
        endDate: z.date({ message: "Lodge end date is required" }),
      }),
    ),
    // Water Taxi fields (Story 2.2) - dynamic array of trips
    waterTaxiTrips: z.array(waterTaxiTripSchema),
    // Kayak Rental fields (Story 2.3) - dynamic array of rentals
    kayakRentals: z.array(kayakRentalSchema),
    // Dietary Requirements fields (Story 2.4) - dynamic array, only for lodge/mixed stays
    dietaryRequirements: z.array(dietaryRequirementSchema),
  })
  .refine(
    (data) => {
      // AC5: Disallow past dates
      const today = getToday()
      return startOfDay(data.arrivalDate) >= today
    },
    {
      message: "Arrival date cannot be in the past",
      path: ["arrivalDate"],
    },
  )
  .refine(
    (data) => {
      // AC4: Departure must be on or after arrival
      return startOfDay(data.departureDate) >= startOfDay(data.arrivalDate)
    },
    {
      message: "Departure date must be on or after arrival date",
      path: ["departureDate"],
    },
  )
  .superRefine((data, ctx) => {
    for (const issue of getLodgeStayValidationIssues(data)) {
      ctx.addIssue({ code: "custom", ...issue })
    }
  })

type FormValues = z.infer<typeof bookingFormSchema>

/**
 * Renders the Kitchen section for every booking type.
 *
 * @param form - The react-hook-form form instance for the booking form
 * @param disabled - If true, disables interactive inputs inside the dietary section
 * @returns A React node containing the Kitchen requirements section
 */
function DietaryRequirementsSection({
  form,
  disabled,
}: {
  form: UseFormReturn<FormValues>
  disabled: boolean
}) {
  const guestType = useWatch({ control: form.control, name: "guestType" })
  const lodgeStays = useWatch({
    control: form.control,
    name: "lodgeStays",
  })
  const arrivalDate = useWatch({ control: form.control, name: "arrivalDate" })
  const departureDate = useWatch({
    control: form.control,
    name: "departureDate",
  })
  const lodgeStayLabels = lodgeStays
    .map(formatLodgeStayFormRange)
    .filter((label): label is string => label !== null)

  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
      <div className="px-4 sm:px-0">
        <h2 className="text-base/7 font-semibold text-foreground">Kitchen</h2>
        <p className="mt-1 text-sm/6 text-muted-foreground">
          Capture dietary needs and allergies for kitchen meal planning.
        </p>
        {guestType === "mixed" && lodgeStayLabels.length > 0 && (
          <div className="mt-2 space-y-1 text-sm font-medium text-primary">
            <p>Meals apply to these lodge portions:</p>
            {lodgeStayLabels.map((label, index) => (
              <p key={`${label}-${index}`}>{label}</p>
            ))}
          </div>
        )}
        {guestType === "camping" && (
          <p className="mt-2 text-sm font-medium text-primary">
            Only explicitly selected arrival or departure lunches appear in the
            Kitchen calendar.
          </p>
        )}
      </div>

      <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
        <div className="px-4 py-6 sm:p-8">
          <DietaryRequirementsForm
            bookingType={guestType}
            arrivalDate={arrivalDate}
            departureDate={departureDate}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * Render the New Booking page with a validated multi-section booking form.
 *
 * The component manages form state and validation, transforms form values to the API shape,
 * submits a booking via the BookingsService, shows success or error toasts, and navigates on success.
 *
 * @returns A React element that renders the New Booking page and its form UI
 */
function NewBookingPage() {
  const router = useRouter()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const form = useForm<FormValues>({
    resolver: zodResolver(bookingFormSchema),
    mode: "onBlur", // Validate on blur for real-time feedback
    criteriaMode: "all",
    defaultValues: {
      guestType: "lodge",
      primaryContactName: "",
      email: "",
      phone: "",
      arrivalDate: undefined,
      departureDate: undefined,
      numberOfGuests: 1,
      lodgeStays: [],
      // Water taxi defaults - empty array means no trips
      waterTaxiTrips: [],
      // Kayak rental defaults - empty array means no rentals
      kayakRentals: [],
      // Dietary requirements defaults - empty array means no dietary info
      dietaryRequirements: [],
    },
  })

  const onSubmit = async (data: FormValues) => {
    try {
      const requestBody = bookingFormValuesToRequest(data)

      await BookingsService.createBooking({ requestBody })
      showSuccessToast("Booking created successfully")
      router.history.push("/bookings")
    } catch (error) {
      showErrorToast("Failed to create booking")
      console.error("Booking creation failed:", error)
    }
  }

  const handleCancel = () => {
    router.history.push("/")
  }

  return (
    <div className="container max-w-5xl py-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, (errors) =>
            console.error("Form validation errors:", errors),
          )}
        >
          <div className="divide-y divide-border">
            {/* Guest Information Section */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0">
                <h2 className="text-base/7 font-semibold text-foreground">
                  Guest Information
                </h2>
                <p className="mt-1 text-sm/6 text-muted-foreground">
                  Enter the primary contact details and stay information for
                  this booking.
                </p>
              </div>

              <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
                <div className="px-4 py-6 sm:p-8">
                  <GuestInformationForm
                    disabled={form.formState.isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* Water Taxi Section (Story 2.2) */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0">
                <h2 className="text-base/7 font-semibold text-foreground">
                  Water Taxi
                </h2>
                <p className="mt-1 text-sm/6 text-muted-foreground">
                  Schedule water taxi transportation for guest arrivals and
                  departures.
                </p>
              </div>

              <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
                <div className="px-4 py-6 sm:p-8">
                  <WaterTaxiForm disabled={form.formState.isSubmitting} />
                </div>
              </div>
            </div>

            {/* Kayak Rental Section (Story 2.3) */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0">
                <h2 className="text-base/7 font-semibold text-foreground">
                  Kayak Rentals
                </h2>
                <p className="mt-1 text-sm/6 text-muted-foreground">
                  Assign kayak equipment to individual guests with pickup and
                  return times.
                </p>
              </div>

              <div className="bg-card shadow-xs outline outline-border sm:rounded-xl md:col-span-2 dark:bg-card/50 dark:shadow-none dark:-outline-offset-1">
                <div className="px-4 py-6 sm:p-8">
                  <KayakRentalForm disabled={form.formState.isSubmitting} />
                </div>
              </div>
            </div>

            {/* Kitchen Section */}
            <DietaryRequirementsSection
              form={form}
              disabled={form.formState.isSubmitting}
            />

            {/* Form Actions */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-8 py-10 md:grid-cols-3">
              <div className="px-4 sm:px-0" />
              <div className="md:col-span-2">
                <div className="flex items-center justify-end gap-x-6 px-4 sm:px-0">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={form.formState.isSubmitting}
                    className="text-sm/6 font-semibold text-foreground hover:text-foreground/80 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <LoadingButton
                    type="submit"
                    loading={form.formState.isSubmitting}
                  >
                    Create Booking
                  </LoadingButton>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
